import { useEffect, useRef, useState } from 'react';
import {
    AlertBox,
    AlertBoxDismissButton,
    AspectRatio,
    Box,
    Button,
    FileTile,
    FileTileDeleteButton,
    FileTileThumbnail,
    FlexBox,
    Icon,
    StandardTileImage,
    Typography,
} from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { useFileUploaderService } from '../contexts/FileUploaderContext';
import { AGENT_IMAGE_MIME_TYPES } from '../hooks/useClaudeTurn';
import { compressImage } from '../utils/imageProcessing';
import { ResizableTextArea } from './ResizableTextArea';
import type { UploadedAttachment } from '../types/selectors';
import type { ResizableTextAreaRef } from './ResizableTextArea';

export interface ChatInputFieldProps {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    /** Currently staged attachments (already uploaded to MCP). */
    attachments?: UploadedAttachment[];
    /** Called with newly uploaded attachments when the user picks files. */
    onAttachmentsAdd?: (added: UploadedAttachment[]) => void;
    /** Remove a staged attachment by its MCP uploadId. */
    onAttachmentRemove?: (uploadId: string) => void;
    disabled?: boolean;
    placeholder?: string;
    inputRef?: React.RefObject<ResizableTextAreaRef | null>;
}

const VALID_MIME_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'application/pdf',
    'application/msword', // .doc
]);
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_UPLOADS = 10;
const LOW_RES_THRESHOLD = 800;

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Read an image file's natural dimensions via an off-DOM Image element.
 * Used purely to surface the low-res-warning AlertBox; failure is non-fatal
 * (we just skip the warning).
 */
function measureImage(file: File): Promise<{ width: number; height: number; longestEdge: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
                longestEdge: Math.max(img.naturalWidth, img.naturalHeight),
            });
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(url);
            reject(e);
        };
        img.src = url;
    });
}

export function ChatInputField({
    value,
    onChange,
    onSend,
    attachments = [],
    onAttachmentsAdd,
    onAttachmentRemove,
    disabled = false,
    placeholder = 'Type your answer here',
    inputRef,
}: ChatInputFieldProps) {
    const internalRef = useRef<ResizableTextAreaRef>(null);
    const textAreaRef = (inputRef ?? internalRef) as React.RefObject<ResizableTextAreaRef>;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [lowResWarning, setLowResWarning] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [pendingCount, setPendingCount] = useState(0);

    const uploader = useFileUploaderService();
    const isUploading = uploader?.loading ?? false;
    const attachmentsEnabled = !!(uploader && onAttachmentsAdd);

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.resize();
        }
    }, [value, textAreaRef]);

    // Surface upload-hook errors into the local AlertBox without leaking the
    // raw Error message (which is often a long signed-URL body).
    useEffect(() => {
        if (uploader?.error) setFileError('Failed to upload image. Please try again.');
    }, [uploader?.error]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    const handleAttachmentClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0 || !uploader || !onAttachmentsAdd) return;
        const files = Array.from(fileList);

        // Always reset the input so the same files can be re-selected if the
        // upload fails or the user removes them.
        e.target.value = '';

        if (attachments.length + files.length > MAX_UPLOADS) {
            setFileError(`You can attach a maximum of ${MAX_UPLOADS} files`);
            return;
        }

        // Validate up front; reject the whole batch on the first failure so the
        // user sees a clear message rather than silent drops.
        for (const file of files) {
            if (!VALID_MIME_TYPES.has(file.type)) {
                setFileError(
                    'Please select supported files only (JPEG, PNG, GIF, BMP, PDF, or DOC)'
                );
                return;
            }
            if (file.size > MAX_FILE_BYTES) {
                setFileError(`"${file.name}" is larger than 10MB`);
                return;
            }
        }

        setFileError(null);

        setPendingCount(files.length);

        // Best-effort low-res check — only for agent-supported image types (JPEG/PNG),
        // and only the smallest in the batch since the warning copy is generic.
        const agentImageFiles = files.filter((f) => AGENT_IMAGE_MIME_TYPES.has(f.type));
        try {
            if (agentImageFiles.length > 0) {
                const measurements = await Promise.all(agentImageFiles.map(measureImage));
                const minEdge = Math.min(...measurements.map((m) => m.longestEdge));
                setLowResWarning(minEdge < LOW_RES_THRESHOLD);
            } else {
                setLowResWarning(false);
            }
        } catch {
            setLowResWarning(false);
        }

        try {
            const [uploaded, compressed] = await Promise.all([
                uploader.uploadFiles(files),
                Promise.all(
                    files.map((f) =>
                        // Only compress/encode agent-supported image types; other files
                        // are uploaded to MCP but not forwarded to the agent as base64.
                        AGENT_IMAGE_MIME_TYPES.has(f.type)
                            ? compressImage(f).catch((err) => {
                                  bugtracker.notify(
                                      new Error(`Failed to compress chat brief image: ${err}`),
                                      (event) => {
                                          event.addMetadata('file', 'name', f.name);
                                          event.addMetadata('file', 'type', f.type);
                                          event.addMetadata('file', 'size', f.size);
                                      }
                                  );
                                  return null;
                              })
                            : Promise.resolve(null)
                    )
                ),
            ]);
            if (uploaded.length === 0) {
                // Hook surfaced an error via the useEffect above; nothing to add.
                return;
            }
            const added: UploadedAttachment[] = uploaded.map((u, i) => ({
                uploadId: u.id,
                previewUrl: u.previewUrl,
                downloadUrl: u.downloadUrl,
                fileName: u.name,
                mediaType: files[i]?.type ?? 'application/octet-stream',
                fileSize: u.originalFileSize,
                base64: compressed[i]?.base64,
                compressedMediaType: compressed[i]?.mediaType,
            }));
            onAttachmentsAdd(added);
        } catch {
            setFileError('Failed to upload image. Please try again.');
        } finally {
            setPendingCount(0);
        }
    };

    const handleRemove = (att: UploadedAttachment) => {
        onAttachmentRemove?.(att.uploadId);
        // Best-effort cleanup; failure is non-fatal and already surfaced by the hook.
        uploader?.deleteFile(att.uploadId).catch(() => {
            /* swallowed — useFileUploader has already notified bugtracker */
        });
    };

    const canSend = !!value.trim() || attachments.length > 0;

    return (
        <>
            {/* Low resolution warning — above input box */}
            {attachments.length > 0 && lowResWarning && (
                <Box style={{ maxWidth: '768px', margin: '0 auto', width: '100%' }} mb={4}>
                    <AlertBox skin="warning" onRequestDismiss={() => setLowResWarning(false)}>
                        One of your images looks quite small — your designer may have trouble seeing
                        the details. Got a higher quality version?
                        <AlertBoxDismissButton />
                    </AlertBox>
                </Box>
            )}

            {/* File error message */}
            {fileError && (
                <Box
                    style={{
                        maxWidth: '768px',
                        margin: '0 auto',
                        width: '100%',
                        marginBottom: 'var(--swan-sem-space-3)',
                    }}
                >
                    <AlertBox skin="error" onRequestDismiss={() => setFileError(null)}>
                        {fileError}
                        <AlertBoxDismissButton accessibleText="Dismiss error" />
                    </AlertBox>
                </Box>
            )}

            <Box
                style={{
                    maxWidth: '768px',
                    margin: '0 auto',
                    width: '100%',
                }}
            >
                {/* Chat inner — bordered container matching Figma */}
                <Box
                    className="chat-input-container"
                    role="group"
                    aria-label="Message input"
                    style={{
                        backgroundColor: 'var(--swan-sem-color-bg-standard)',
                        borderRadius: 'var(--swan-sem-border-radius-input)',
                        boxShadow:
                            'inset 0 0 0 var(--swan-sem-border-width-input) var(--swan-sem-color-border-input)',
                    }}
                >
                    {/* Attachment previews — one FileTile per uploaded image */}
                    {(attachments.length > 0 || (isUploading && pendingCount > 0)) && (
                        <Box pt={4} px={4}>
                            <FlexBox gap={3} style={{ flexWrap: 'wrap' }}>
                                {attachments.map((att) => (
                                    <FileTile
                                        key={att.uploadId}
                                        fileName={att.fileName}
                                        fileDescription={
                                            att.fileSize
                                                ? formatFileSize(Number(att.fileSize))
                                                : undefined
                                        }
                                        style={{ width: '120px' }}
                                    >
                                        <FileTileThumbnail>
                                            {att.mediaType.startsWith('image/') ? (
                                                <img src={att.previewUrl} alt={att.fileName} />
                                            ) : (
                                                <Icon iconType="fileGeneric" />
                                            )}
                                        </FileTileThumbnail>
                                        <FileTileDeleteButton onClick={() => handleRemove(att)} />
                                    </FileTile>
                                ))}
                                {isUploading &&
                                    Array.from({ length: pendingCount }).map((_, i) => (
                                        <Box
                                            key={`skeleton-${i}`}
                                            aria-busy="true"
                                            style={{ width: '120px' }}
                                        >
                                            <StandardTileImage>
                                                <AspectRatio ratio={1} loadingShimmer />
                                            </StandardTileImage>
                                        </Box>
                                    ))}
                            </FlexBox>
                        </Box>
                    )}

                    {/* Textarea + overlaid buttons — same grid layout as before */}
                    <Box
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            alignItems: 'end',
                            width: '100%',
                        }}
                    >
                        <Box
                            style={{
                                position: 'relative',
                                resize: 'none',
                                overflow: 'visible',
                                gridColumn: '1 / -1',
                                width: '100%',
                            }}
                        >
                            <ResizableTextArea
                                ref={textAreaRef}
                                aria-label="Type your message"
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={placeholder}
                                rows={1}
                                pb={10}
                                pr={5}
                                pt={4}
                                style={{
                                    resize: 'none',
                                    overflow: 'auto',
                                    width: '100%',
                                }}
                                fullWidth
                                disabled={disabled}
                            />
                        </Box>
                        <Box
                            style={{
                                gridColumn: '2',
                                justifySelf: 'end',
                                marginTop: '-10em',
                            }}
                        >
                            <Box
                                mt={{ xs: 10, sm: 4 }}
                                m={4}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--swan-sem-space-between-actions)',
                                }}
                            >
                                {attachmentsEnabled && (
                                    <>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept="image/jpeg,image/png,image/gif,image/bmp,application/pdf,application/msword"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        <Button
                                            skin="tertiary"
                                            buttonShape="round"
                                            onClick={handleAttachmentClick}
                                            aria-label="Attach image"
                                            disabled={
                                                disabled ||
                                                isUploading ||
                                                attachments.length >= MAX_UPLOADS
                                            }
                                        >
                                            <Icon iconType="attachment" />
                                        </Button>
                                    </>
                                )}
                                <Button
                                    skin="primary"
                                    buttonShape="round"
                                    onClick={onSend}
                                    disabled={!canSend || disabled || isUploading}
                                    aria-label="Send message"
                                >
                                    <Icon iconType="send" />
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Typography
                fontSkin="footnote"
                textColor="subtle"
                mt={2}
                style={{ textAlign: 'center' }}
            >
                Powered by AI. Please double check responses.
            </Typography>
        </>
    );
}
